import React, { useState } from 'react';
import { X, User, Mail, Camera } from 'lucide-react';

const EditProfileModal = ({ 
  isOpen, 
  onClose, 
  userProfile, 
  setUserProfile,
  handleProfilePicUpload,
  showToast
}) => {
  const [localName, setLocalName] = useState(userProfile.name || '');
  const [localEmail, setLocalEmail] = useState(userProfile.email || '');

  if (!isOpen) return null;

  const handleSaveProfile = () => {
    if (!localName.trim()) {
      showToast('Please enter your name', 'warning');
      return;
    }

    if (!localEmail.trim()) {
      showToast('Please enter your email', 'warning');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(localEmail)) {
      showToast('Please enter a valid email address', 'warning');
      return;
    }

    setUserProfile({
      ...userProfile,
      name: localName,
      email: localEmail
    });

    showToast('Profile updated successfully!', 'success');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Edit Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:bg-gray-100 rounded-full p-2 transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Profile Picture Section */}
          <div className="flex flex-col items-center">
            <div className="relative">
              {userProfile.profilePic ? (
                <img 
                  src={userProfile.profilePic} 
                  alt="Profile" 
                  className="w-24 h-24 rounded-full object-cover border-4 border-blue-100"
                />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center border-4 border-blue-100">
                  <User size={40} className="text-blue-600" />
                </div>
              )}
              
              <label className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-all shadow-lg">
                <Camera size={16} />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePicUpload}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">Click camera to change photo</p>
          </div>

          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <div className="relative">
             <input
                type="text"
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          {/* Email Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <input
                type="email"
                value={localEmail}
                onChange={(e) => setLocalEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>

          {/* Current Info Display */}
          {userProfile.address && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-800 text-sm mb-2">
                Additional Information
              </h4>
              <div className="space-y-1">
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Address:</span> {userProfile.address}
                </p>
                {userProfile.pincode && (
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Pincode:</span> {userProfile.pincode}
                  </p>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Use "Edit Address" to update delivery address
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
            >
              Save Changes
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">
            👤 Profile Settings
          </h4>
          <p className="text-xs text-gray-600">
            Keep your profile information up to date for personalized service and better communication.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;